/**
 * Ads — manual editing of the `ads.txt` and `app-ads.txt` authorized-sellers
 * files served from the frontend `$web` container.
 *
 * Files live under `config/<env>/` in the shared `trfrontendstorage` `$web`
 * container (same account as the company logos), e.g.
 *   config/prod/ads.txt
 *   config/prod/app-ads.txt
 *   config/preprod/ads.txt
 *   config/preprod/app-ads.txt
 *
 * The environment is NOT chosen in the UI — it is fixed per deployment and
 * resolved server-side from `APP_VER`: the prod admin (admin.tipranks.com)
 * edits `config/prod/`, the preprod admin (admin-preprod.tipranks.com) edits
 * `config/preprod/`.
 *
 * Unlike logo uploads, these files are meant to be edited in place, so saving
 * OVERWRITES the existing blob.
 */

/** Deployment environments the files exist for. */
export const ADS_ENVIRONMENTS = ["prod", "preprod"] as const;
export type AdsEnvironment = (typeof ADS_ENVIRONMENTS)[number];

/** The two editable files. */
export const ADS_FILE_NAMES = ["ads.txt", "app-ads.txt"] as const;
export type AdsFileName = (typeof ADS_FILE_NAMES)[number];

/** Folder inside the `$web` container that holds the per-env config. */
export const ADS_BLOB_PREFIX = "config";

export function isValidAdsEnvironment(v: unknown): v is AdsEnvironment {
  return (
    typeof v === "string" && ADS_ENVIRONMENTS.includes(v as AdsEnvironment)
  );
}

export function isValidAdsFileName(v: unknown): v is AdsFileName {
  return typeof v === "string" && ADS_FILE_NAMES.includes(v as AdsFileName);
}

/** `("prod", "ads.txt")` → `config/prod/ads.txt` */
export function adsBlobName(env: AdsEnvironment, file: AdsFileName): string {
  return `${ADS_BLOB_PREFIX}/${env}/${file}`;
}

export interface GetAdsFileResponse {
  env: AdsEnvironment;
  file: AdsFileName;
  blobName: string;
  /** Current file contents, or "" if the blob does not exist yet. */
  content: string;
  /** Whether the blob currently exists in storage. */
  exists: boolean;
}

export interface UpdateAdsFileRequest {
  file: AdsFileName;
  /** The full new file contents (overwrites the existing blob). */
  content: string;
}

export interface UpdateAdsFileResponse {
  env: AdsEnvironment;
  file: AdsFileName;
  blobName: string;
}
